/**
 * lazy-mount.js
 *
 * Defers rendering of heavy, below-the-fold section content so its <img>/<video>
 * elements stay OUT of the live DOM (and off the network) until the user scrolls
 * near. This reduces initial DOM <img> count (Phase 2 DOM-reduction target) and
 * main-thread image decode/layout work.
 *
 * Markup contract (emitted by sections that opt in):
 *   <div class="lazy-mount" data-lazy-mount style="min-height:{reserved}px">
 *     <template data-lazy-mount-content> …heavy markup with <img>… </template>
 *   </div>
 *
 * On IntersectionObserver (with a generous rootMargin so it mounts BEFORE the
 * block is visible — keeps cumulative layout shift off-screen), the template's
 * content is cloned into the container, the template + reserved min-height are
 * removed, and `shopify:section:load` is dispatched on the nearest section so
 * existing init code (media-autoplay, etc.) re-runs against the new nodes.
 *
 * Theme editor: sections render their content directly (no template) when
 * `request.design_mode` is true, so this script is a no-op there.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-lazy-mount]';
  // Mount well before the block enters the viewport so the height change
  // happens off-screen and does not count against CLS.
  var ROOT_MARGIN = '600px 0px';

  function mount(container) {
    var tpl = container.querySelector('template[data-lazy-mount-content]');
    if (!tpl) return;

    var fragment = tpl.content.cloneNode(true);
    // Insert cloned content before the template, then drop the template and the
    // reserved-height hint so the real content drives layout from here on.
    container.insertBefore(fragment, tpl);
    tpl.remove();
    container.removeAttribute('data-lazy-mount');
    // Drop the reserved-height floor (inline overrides the responsive CSS
    // min-height); mounted content drives layout from here on. Inline 0 makes
    // this robust even if the CSS reservation was an over-estimate.
    container.style.minHeight = '0px';

    // Let existing per-section init (media-autoplay, etc.) wire up the new nodes.
    var section = container.closest('.shopify-section') || container;
    document.dispatchEvent(
      new CustomEvent('shopify:section:load', { detail: { sectionId: section.id || '' } })
    );
  }

  function init(root) {
    var containers = (root || document).querySelectorAll(SELECTOR);
    if (!containers.length) return;

    if (!('IntersectionObserver' in window)) {
      // No observer support: mount everything immediately (graceful degradation).
      for (var i = 0; i < containers.length; i++) mount(containers[i]);
      return;
    }

    var observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          obs.unobserve(entry.target);
          mount(entry.target);
        }
      });
    }, { rootMargin: ROOT_MARGIN });

    for (var j = 0; j < containers.length; j++) observer.observe(containers[j]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor: a re-rendered section may contain new lazy-mount containers.
  document.addEventListener('shopify:section:load', function (e) {
    if (e && e.target) init(e.target);
  });
})();
